import { css } from "@mds/core";

export const buttonCSS = css`
	/* 	###################################################################### */
	/**	Component tokens
			 Using the system tokens to populated the component tokens.
			 No white label (ie brand/figma tokens) are allowed here.
		 */
	:host(mds-button) {
		--display: var(
			--button-display,
			inline-block
		); /* --button-display can be set by a parent component */

		--line-width: var(--mds-line-border-base-weight);
		--line-width-vertical: var(--mds-line-border-base-weight);
		--line-width-horizontal: var(--mds-line-border-base-weight);
		--line-style: var(--mds-line-border-base-style);
		--line-color: var(--mds-color-transparent);
		--radius: var(--mds-line-border-base-radius);
		--underline-color: var(--line-color);

		--font-size: var(--mds-typography-sizes-body);
		--text-color: var(--mds-color-text-link-inverse);
		--background-color: var(--mds-color-background-cta);

		--icon-position: -1;
		--icon-glyph: var(--mds-icon-glyphs-base-right-arrow);
		--icon-color: var(--mds-color-text-link-inverse);

		--padding-vertical: var(--mds-spacing-3);
		--padding-horizontal: var(--mds-spacing-3);
		--width: var(--button-width, auto);

		/* TODO: create a motion design token */
		--animation-transition-fade: all 0.3s linear;
		--animation-transition-movement: all 0.1s linear;
	}

	:host(mds-button) > :hover {
		--background-color: var(--mds-color-background-cta-hover);
	}

	/* 	###################################################################### */
	/**	Button rules
			This is the main rule set.
		 */
	:host(mds-button) {
		padding: 0;
		margin: 0;
		display: var(--display);
		//	overflow: hidden;
		border-radius: var(--radius);
		font-size: var(--font-size);
		width: var(--width);
		background-color: transparent;
	}
	:host(mds-button:not(:last-of-type)) {
		margin-right: var(--padding-horizontal);
	}

	.mds-btn {
		margin: 0;
		position: relative;
		display: var(--display);
		border-style: var(--line-style) var(--line-style) var(--line-style)
			var(--line-style);
		border-width: var(--line-width-vertical) var(--line-width-horizontal)
			var(--line-width-vertical) var(--line-width-horizontal);
		border-color: var(--line-color) var(--line-color) var(--underline-color)
			var(--line-color);
		border-radius: var(--radius);
		padding: var(--padding-vertical) var(--padding-horizontal);
		width: var(--width);
		color: var(--text-color);
		background-color: var(--background-color);
		text-decoration: none;
		font-family: var(--mds-typography-font-gui-fonts);
		font-weight: var(--mds-typography-font-gui-weight-normal);
		line-height: var(--mds-typography-scale-height-l);
		cursor: pointer;
		font-size: 1em;
		vertical-align: top;
	}

	:host(mds-button) a:link,
	:host(mds-button) a:active,
	:host(mds-button) a:visited,
	:host(mds-button) a:hover {
		border-color: var(--line-color) var(--line-color) var(--underline-color)
			var(--line-color);
		color: var(--text-color);
		transition: var(--animation-transition-fade);
	}
	:host .mds-btn:focus-visible {
		transition: none;
	}
	:host(mds-button[context="subtle"]) > .mds-btn {
		text-align: start;
	}
	:host(mds-button[ondark]) .mds-btn:focus-visible {
		outline-color: var(--mds-color-border-focus-inverse);
	}

	:host(mds-button[disabled]) > .mds-btn:hover {
		cursor: not-allowed;
	}
	:host(mds-button[compact]),
	:host(mds-button[compact]) .mds-btn {
		padding-block: 0;
	}

	/* 	###################################################################### */
	/**	Properties for the Brand button */
	:host(mds-button[context="brand"]) {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-cta);
	}

	:host(mds-button[context="brand"]) > :hover {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-cta-hover);
	}

	/**	Properties for the Brand ondark button */
	:host(mds-button[context="brand"][ondark]) {
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--background-color: var(--mds-color-background-neutral);
	}

	:host(mds-button[context="brand"][ondark]) > :hover {
		--text-color: var(--mds-color-text-link);
		--background-color: var(--mds-color-background-cta-inverse-hover);
	}

	/* 	###################################################################### */
	/**	Properties for the Neutral button component */
	:host(mds-button[context="neutral"]) {
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--line-color: var(--mds-color-border-neutral);
		--background-color: var(--mds-color-background-neutral);
	}

	:host(mds-button[context="neutral"]) > :hover {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-inverse);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-neutral-hover);
	}

	/**	Properties for the Secondary ondark button */
	:host(mds-button[context="neutral"][ondark]) {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-border-neutral-inverse);
		--background-color: var(--mds-color-background-neutral-inverse);
	}

	:host(mds-button[context="neutral"][ondark]) > :hover {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-border-neutral-inverse);
		--underline-color: var(--mds-color-border-neutral-inverse);
		--background-color: var(--mds-color-background-neutral-inverse-hover);
	}

	/* 	###################################################################### */
	/**	Properties for subtle buttons */
	:host(mds-button[context="subtle"]) > .mds-btn {
		--padding-horizontal: 0;
		--line-width-horizontal: 0;
		--radius: 0;
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-transparent) !important;
	}

	:host(mds-button[context="subtle"]) > .mds-btn:hover {
		--icon-color: var(--mds-color-text-link-hover);
		--text-color: var(--mds-color-text-link-hover);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-transparent);
	}

	/**	Properties for the subtle ondark button */
	:host(mds-button[context="subtle"][ondark]) .mds-btn {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-transparent);
	}

	:host(mds-button[context="subtle"][ondark]) > .mds-btn:hover {
		--icon-color: var(--mds-color-text-link-inverse-hover);
		--text-color: var(--mds-color-text-link-inverse-hover);
		--line-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-transparent);
	}

	/* 	###################################################################### */
	/**	Properties for the Neutral button & button used in tabs component */
	:host(mds-button[context="tabs"]) {
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--line-color: var(--mds-color-border-neutral);
		--background-color: var(--mds-color-background-neutral);
	}

	:host(mds-button[context="tabs"][aria-selected="true"]),
	:host(mds-button[context="tabs"][aria-selected="true"][icon]) {
		--icon-color: var(--mds-color-link-inverse);
		--line-color: var(--mds-color-border-neutral);
		--text-color: var(--mds-color-text-link-inverse);
		--background-color: var(--mds-color-background-default);
	}

	:host(mds-button[context="tabs"][aria-selected="false"]) > :hover {
		--icon-color: var(--mds-color-text-link-hover);
		--text-color: var(--mds-color-text-link-hover);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-neutral-subtle-hover);
	}

	/* 	###################################################################### */
	/**	Properties for disabled buttons */
	:host(mds-button[disabled]),
	:host(mds-button[disabled]) > :hover {
		--icon-color: var(--mds-color-text-disabled);
		--text-color: var(--mds-color-text-disabled);
		--line-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-disabled);
	}

	/**	Properties for the disabled ondark button */
	:host(mds-button[disabled][ondark]),
	:host(mds-button[disabled][ondark]) > :hover {
		--icon-color: var(--mds-color-text-disabled-inverse);
		--text-color: var(--mds-color-text-disabled-inverse);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-disabled-inverse);
	}

	:host(mds-button[context="subtle"][disabled]) .mds-btn {
		--icon-color: var(--mds-color-text-disabled);
		--text-color: var(--mds-color-text-disabled);
	}

	/* 	###################################################################### */
	/**	Properties for the subtle Link */
	:host(mds-button[context="subtle"][type="link"]) {
		--padding-horizontal: 0;
		--line-width-horizontal: 0;
		--radius: 0;
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-transparent);
	}

	:host(mds-button[context="subtle"][type="link"]) > :hover {
		--icon-color: var(--mds-color-text-link-hover);
		--text-color: var(--mds-color-text-link-hover);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-transparent);
	}

	:host(mds-button[context="subtle"][type="link"][ondark]) {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-transparent);
	}

	:host(mds-button[context="subtle"][type="link"][ondark]) > :hover {
		--icon-color: var(--mds-color-text-link-inverse-hover);
		--text-color: var(--mds-color-text-link-inverse-hover);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-transparent);
	}

	/* 	###################################################################### */
	/**	Properties for the Emphasis Link */
	:host(mds-button[context="emphasis"][type="link"]) a {
		--padding-horizontal: 0;
		--line-width-horizontal: 0;
		--radius: 0;
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-border-link);
		--background-color: var(--mds-color-transparent);
	}

	:host(mds-button[context="emphasis"][type="link"]) > :hover {
		--icon-color: var(--mds-color-text-link-hover);
		--text-color: var(--mds-color-text-link-hover);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-border-link-hover);
		--background-color: var(--mds-color-transparent);
	}

	:host(mds-button[context="emphasis"][type="link"][ondark]) a {
		--text-color: var(--mds-color-text-link-inverse);
		--icon-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-border-link-inverse);
		--background-color: var(--mds-color-transparent);
	}

	:host(mds-button[context="emphasis"][type="link"][ondark]) > :hover {
		--icon-color: var(--mds-color-text-link-inverse-hover);
		--text-color: var(--mds-color-text-link-inverse-hover);
		--underline-color: var(--mds-color-border-link-inverse-hover);
		--background-color: var(--mds-color-transparent);
	}

	/* 	###################################################################### */
	/**	Properties for the Brand link */
	:host(mds-button[context="brand"][type="link"]) {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-cta);
	}

	:host(mds-button[context="brand"][type="link"]) > :hover {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-cta-hover);
	}

	/**	Properties for the CTA ondark button */
	:host(mds-button[context="brand"][type="link"][ondark]) {
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-cta-inverse);
	}

	:host(mds-button[context="brand"][type="link"][ondark]) > :hover {
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--line-color: var(--mds-color-transparent);
		--underline-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-cta-inverse-hover);
	}

	/* 	###################################################################### */
	/**	Properties for the Neutral link */
	:host(mds-button[context="neutral"][type="link"]) a {
		--icon-color: var(--mds-color-text-link);
		--text-color: var(--mds-color-text-link);
		--line-color: var(--mds-color-border-neutral);
		--underline-color: var(--mds-color-border-neutral);
		--background-color: var(--mds-color-background-neutral);
	}

	:host(mds-button[context="neutral"][type="link"]) > :hover {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-text-link);
		--underline-color: var(--mds-color-text-link);
		--background-color: var(--mds-color-background-neutral-hover);
	}

	/**	Properties for the Secondary ondark button */
	:host(mds-button[context="neutral"][type="link"][ondark]) a {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-border-neutral-inverse);
		--underline-color: var(--mds-color-border-neutral-inverse);
		--background-color: var(--mds-color-background-neutral-inverse);
	}

	:host(mds-button[context="neutral"][type="link"][ondark]) > :hover {
		--icon-color: var(--mds-color-text-link-inverse);
		--text-color: var(--mds-color-text-link-inverse);
		--line-color: var(--mds-color-border-neutral-inverse);
		--underline-color: var(--mds-color-border-neutral-inverse);
		--background-color: var(--mds-color-background-neutral-inverse-hover);
	}

	/* 	###################################################################### */
	/**	Rules for the button icons */

	:host(mds-button[icon]) .mds-btn {
		display: var(--display, grid);
		grid-template-columns: auto auto;
		grid-template-rows: 1fr;
		align-items: baseline;
	}
	:host(mds-button[icon]) a.mds-btn {
		vertical-align: baseline;
	}
	:host .mds-btn * {
		grid-row: 1;
	}

	:host(mds-button[icon][position="start"]) ::slotted(*) {
		padding-left: var(--mds-spacing-2);
	}

	:host(mds-button[icon][position="end"]) ::slotted(*) {
		padding-right: var(--mds-spacing-2);
	}
	:host(mds-button[icon]) .mds-btn::before,
	:host(mds-button[icon]) .mds-btn::after {
		font-family: var(--mds-icon-font-base-family);
		font-weight: normal;
		grid-row: 1;
		grid-column: var(--icon-position);
		color: var(--icon-color);
		line-height: 1;
	}
	:host(mds-button[icon][position="end"]) .mds-btn::after,
	:host(mds-button[icon][position="start"]) .mds-btn::before {
		content: var(--icon-glyph);
	}

	:host(mds-button[type="link"]:not([icon])) .mds-btn {
		display: grid;
		grid-template-columns: auto auto;
		grid-template-rows: 1fr;
		align-items: center;
		padding-right: var(--mds-spacing-2);
	}

	:host(mds-button[type="link"][context="brand"]) .mds-btn,
	:host(mds-button[type="link"][context="neutral"]) .mds-btn {
		padding-right: var(--mds-spacing-4);
	}

	:host(mds-button[type="link"]:not([icon])) .mds-btn ::slotted(*) {
		padding-right: var(--mds-spacing-2);
	}

	:host(mds-button[type="link"]:not([icon])) .mds-btn::before {
		font-weight: normal;
		position: relative;
		left: 0;
		transition: var(--animation-transition-movement);
		--icon-glyph: var(--mds-icon-glyphs-base-right-arrow);
		--icon-position: -1;
	}

	:host(mds-button[type="link"]:not([icon])) .mds-btn::before {
		font-family: var(--mds-icon-font-base-family);
		font-weight: normal;
		content: var(--icon-glyph);
		grid-row: 1;
		grid-column: var(--icon-position);
		color: var(--icon-color);
	}

	:host(mds-button[type="link"]:not([icon])) .mds-btn:hover::before {
		left: var(--mds-spacing-2);
	}
`;
